import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { SubjectsSelected } from '../../../ampli';
import OnboardingStepSchulfaecherImage from '../../../assets/onboarding/onboarding-step-schulfaecher.svg';
import constants, { NONE_OF_THE_ABOVE_OPTION } from '../../../constants';
import { selectUser } from '../../../reducers/user.selectors';
import type { OnboardingSteps } from '../../../routes/onboarding/OnboardingPage';
import services from '../../../services';
import OnboardingOptionChipList, { type Option } from '../OnboardingOptionChipList';
import OnboardingStepContainer from '../OnboardingStepContainer';
import OnboardingStepImage from '../OnboardingStepImage';

interface OwnProps {
	setOnboardingStep: (step: keyof OnboardingSteps) => void;
	setInitialSelectedSubjects: (subjects: Option[]) => void;
	stepNum?: number;
	totalSteps?: number;
	onBackOverride?: (() => void) | null;
	onNextOverride?: () => void;
	hideImage?: boolean;
	compact?: boolean;
	subtitle?: string;
}

const OnboardingStepSchulfaecher: React.FC<OwnProps> = ({
	setOnboardingStep,
	setInitialSelectedSubjects,
	stepNum,
	totalSteps,
	onBackOverride,
	onNextOverride,
	hideImage,
	compact,
	subtitle,
}) => {
	const user = useSelector(selectUser);
	const [schulfaecher, setSchulfaecher] = React.useState<Option[]>([]);
	const { t } = useTranslation();
	const hasOptionsSelected = schulfaecher.some((option) => option.isSelected);

	const contentHeights = {
		default: 'calc(100vh - 503px)',
		smallScreen: 'calc(100vh - 309px)',
	};

	useEffect(() => {
		const userSubjectIds = (user?.subjectsFlattened ?? []).map((s) => s.id);
		const subjectList: Option[] = constants.subjects
			.filter((subject) => subject.id !== NONE_OF_THE_ABOVE_OPTION)
			.map((subject) => ({
				name: subject.text,
				value: subject.id,
				isSelected: userSubjectIds.includes(subject.id),
				iconName: subject.iconName,
			}))
			.sort((a, b) => a.name.localeCompare(b.name));
		setSchulfaecher(subjectList);
	}, [user?.subjectsFlattened]);

	const selectOption = (value: string) => {
		setSchulfaecher((prevStates) =>
			prevStates.map((state) =>
				state.value === value ? { ...state, isSelected: !state.isSelected } : state
			)
		);
	};

	const sendSelectedSubjectEvent = (allSelectedSubjects: Option[]) => {
		if (user) {
			const subjects = {
				majors: allSelectedSubjects.map((subject, i) => ({
					subject: subject.value,
					priority: i,
				})),
				minors: [],
			};
			services.track.ampliEventTrack({
				event: new SubjectsSelected(),
				userProperties: {
					id: user.id,
					subjects,
					isSubjectSelected: true,
				},
			});
		}
	};

	const onClickNext = () => {
		const allSelectedSubjects = schulfaecher.filter((o) => o.isSelected);
		const userSubjectIds = (user?.subjectsFlattened ?? []).map((s) => s.id);
		const sortedSubjects = [
			...allSelectedSubjects
				.filter((s) => userSubjectIds.includes(s.value))
				.sort((a, b) => userSubjectIds.indexOf(a.value) - userSubjectIds.indexOf(b.value)),
			...allSelectedSubjects.filter((s) => !userSubjectIds.includes(s.value)),
		];
		setInitialSelectedSubjects(sortedSubjects);
		sendSelectedSubjectEvent(sortedSubjects);
		if (onNextOverride) {
			onNextOverride();
		} else {
			setOnboardingStep('wichtigkeit');
		}
	};

	const defaultOnBack = () => setOnboardingStep('schulstufe');

	return (
		<>
			<OnboardingStepContainer
				title={t('What school subjects do you teach?')}
				subtitle={subtitle ? t(subtitle) : t('Select one or more school subjects.')}
				stepNum={stepNum ?? 5}
				totalSteps={totalSteps}
				onNext={onClickNext}
				onBack={onBackOverride === undefined ? defaultOnBack : (onBackOverride ?? undefined)}
				onNextIsDisabled={!hasOptionsSelected}
				contentHeights={contentHeights}
				compact={compact}
			>
				<OnboardingOptionChipList options={schulfaecher} onItemClick={selectOption} />
			</OnboardingStepContainer>
			{!hideImage && <OnboardingStepImage stepImage={<OnboardingStepSchulfaecherImage />} />}
		</>
	);
};

export default OnboardingStepSchulfaecher;
